// Split Stop Modal — Déplacer un arrêt vers une autre commande (ou nouvelle)
import { useState, useMemo } from 'react';
import { X, Search, ArrowRight, Plus, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const SplitStopModal = ({ sourceOrder, allOrders, preselectStopId = null, onClose, onMove }) => {
  const [step, setStep] = useState(preselectStopId ? 'select-target' : 'select-stop');
  const [selectedStopId, setSelectedStopId] = useState(preselectStopId);
  const [search, setSearch] = useState('');
  const [moving, setMoving] = useState(false);

  const stops = sourceOrder.stops || [];
  const canSplit = stops.length >= 2; // Il faut au moins 2 stops pour diviser

  const otherOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allOrders.filter((o) => o.id !== sourceOrder.id).filter((o) => {
      if (!q) return true;
      const firstStop = o.stops?.[0] || {};
      const hay = `${o.order_number || ''} ${firstStop.client_name || ''} ${firstStop.city || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allOrders, search, sourceOrder.id]);

  const handlePickStop = (stopId) => {
    setSelectedStopId(stopId);
    setStep('select-target');
  };

  const doMove = async (targetOrderId) => {
    setMoving(true);
    const ok = await onMove(selectedStopId, targetOrderId);
    setMoving(false);
    if (ok) {
      toast.success(targetOrderId ? 'Arrêt déplacé' : 'Arrêt déplacé vers nouvelle commande');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="split-stop-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-base">
              {step === 'select-stop' ? 'Diviser la commande' : 'Vers quelle commande ?'}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {step === 'select-stop'
                ? `${sourceOrder.order_number} • ${stops.length} arrêt(s)`
                : 'Choisir une destination'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white min-h-11 min-w-11" data-testid="close-split-modal"><X size={20} /></button>
        </div>

        {step === 'select-stop' && (
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {!canSplit && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300">
                Impossible de diviser : cette commande n'a qu'un seul arrêt. Une commande doit conserver au moins un arrêt.
              </div>
            )}
            {canSplit && <p className="text-xs text-slate-400 mb-2">Sélectionnez l'arrêt à déplacer :</p>}
            {stops.map((stop, idx) => (
              <button
                key={stop.id}
                onClick={() => canSplit && handlePickStop(stop.id)}
                disabled={!canSplit}
                className={`w-full text-left bg-slate-800 hover:bg-amber-500/20 hover:border-amber-500 border border-slate-700 rounded-lg p-3 flex items-center gap-3 transition-colors min-h-16 disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid={`pick-stop-${stop.id}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold shrink-0 text-xs ${
                  (stop.stop_type === 'cueillette' || stop.stop_type === 'pickup') ? 'bg-blue-500/30 text-blue-300' : 'bg-cyan-500/30 text-cyan-300'
                }`}>{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm truncate">{stop.client_name || '—'}</div>
                  <div className="text-xs text-slate-400 truncate flex items-center gap-1">
                    <MapPin size={10} />
                    {(stop.stop_type === 'cueillette' || stop.stop_type === 'pickup') ? 'CUEILL.' : 'LIVR.'} • {stop.city || '?'} • {stop.required_time || '?'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {step === 'select-target' && (
          <>
            <div className="p-3 border-b border-slate-800 space-y-2">
              <button
                onClick={() => doMove(null)}
                disabled={moving}
                className="w-full bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-300 rounded-lg p-3 font-semibold flex items-center justify-center gap-2 min-h-14 disabled:opacity-50"
                data-testid="split-to-new-order"
              >
                <Plus size={16} />
                <span>→ Créer une nouvelle commande vide</span>
              </button>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Chercher une commande existante (PO, client, ville)…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 text-white pl-9 pr-3 py-2 rounded-lg text-xs placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  data-testid="target-order-search"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {otherOrders.length === 0 && <p className="text-slate-500 text-sm text-center py-8">Aucune autre commande.</p>}
              {otherOrders.map((o) => {
                const first = (o.stops || [])[0] || {};
                return (
                  <button
                    key={o.id}
                    onClick={() => doMove(o.id)}
                    disabled={moving}
                    className="w-full text-left bg-slate-800 hover:bg-amber-500/20 hover:border-amber-500 border border-slate-700 rounded-lg p-2.5 flex items-center gap-2 transition-colors min-h-14 disabled:opacity-50"
                    data-testid={`target-order-${o.id}`}
                  >
                    <ArrowRight size={13} className="text-amber-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-white text-xs font-mono">{o.order_number}</div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {first.client_name || '—'} • {(o.stops || []).length} arrêt(s)
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="p-3 border-t border-slate-800">
              <button
                onClick={() => setStep('select-stop')}
                className="text-slate-400 hover:text-white text-xs"
                data-testid="split-back-btn"
              >
                ← Retour au choix d'arrêt
              </button>
            </div>
          </>
        )}

        {moving && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-t-2xl sm:rounded-xl">
            <Loader2 className="animate-spin text-amber-500" size={32} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SplitStopModal;
